import * as React from "react"
import { useState, useEffect, useCallback } from "react"
import {
    Box, Grid, Card, CardContent, Typography, TextField, Tooltip, Tabs, Tab, MenuItem,
    Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, LinearProgress
} from "@mui/material"
import { AutocompleteAjaxSeeker, IconSeeker } from "@ui-seeker/core"
import { useAppToast } from "@ui-seeker/app"
import { getSubmit } from "@ui-seeker/utils"

import { CATEGORIES_ROUTES, BACKEND_APIS } from "@constants"
import PageWrapper from "Pages/@PageWrapper"

const RB = BACKEND_APIS.RETAILER_BRANDS
const idOf = (v) => (v && typeof v === 'object' ? v.value : v)

// stable color per level-1 vertical
const LEVEL1_COLORS = { beauty: '#c2185b', electronics: '#1565c0', home: '#2e7d32' }

function CategoryLabel({ cat }) {
    if (cat === '(sin categorizar)') {
        return <Typography variant="body2" color="text.disabled">— sin categorizar</Typography>
    }
    const [l1, l2] = cat.split('/')
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LEVEL1_COLORS[l1] || '#757575' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {l1}<Typography component="span" variant="body2" color="text.secondary"> / {l2 || '—'}</Typography>
            </Typography>
        </Box>
    )
}

export default function CategoriesPage() {
    const { showToast } = useAppToast()
    const [customer, setCustomer] = useState(null)
    const [endDate, setEndDate] = useState('')
    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [tab, setTab] = useState(0)            // 0 = overview, 1 = by competitor
    const [compSite, setCompSite] = useState('') // selected competitor site (tab 1)
    const cid = idOf(customer)

    // competitor site names present in the loaded data (no extra endpoint)
    const compSites = React.useMemo(() => {
        const names = new Set()
        rows.forEach(r => (r.breakdown || []).forEach(b => names.add(b.site)))
        return [...names].sort()
    }, [rows])

    useEffect(() => {
        if (compSites.length && !compSites.includes(compSite)) setCompSite(compSites[0])
    }, [compSites])

    // rows reshaped for the selected competitor site
    const siteRows = React.useMemo(() => {
        if (!compSite) return []
        return rows
            .map(r => ({ category: r.category,
                         count: (r.breakdown || []).find(b => b.site === compSite)?.count || 0 }))
            .filter(r => r.count > 0)
            .sort((a, b) => b.count - a.count)
    }, [rows, compSite])
    const siteTotal = siteRows.reduce((s2, r) => s2 + r.count, 0)

    const load = useCallback(() => {
        if (!cid) { setRows([]); return }
        setLoading(true)
        const params = new URLSearchParams({ customerID: cid })
        if (endDate) params.set('endDate', endDate)
        getSubmit(`${RB}/categories?${params.toString()}`)
            .then(res => setRows(res?.categories || []))
            .catch(() => { showToast.error('Could not load categories'); setRows([]) })
            .finally(() => setLoading(false))
    }, [cid, endDate])

    useEffect(() => { load() }, [load])

    const totalOwn = rows.reduce((s, r) => s + (r.own || 0), 0)
    const totalComp = rows.reduce((s, r) => s + (r.competitors || 0), 0)
    const uncategorized = rows.find(r => r.category === '(sin categorizar)')
    const covered = totalOwn + totalComp - ((uncategorized?.own || 0) + (uncategorized?.competitors || 0))
    const coverage = (totalOwn + totalComp) > 0 ? covered / (totalOwn + totalComp) : 0

    return (
        <PageWrapper
            head={CATEGORIES_ROUTES.TITLE}
            title={CATEGORIES_ROUTES.TITLE}
            subtitle={CATEGORIES_ROUTES.SUBTITLE}
            actions={null}
        >
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Card variant="outlined">
                        <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 260 }}>
                                <AutocompleteAjaxSeeker
                                    urlAjax={`${BACKEND_APIS.BATCH_ADMIN}/customers`}
                                    filters={{ clientType: 'retailer' }}
                                    label="Customer"
                                    placeholder="Select a customer…"
                                    value={customer}
                                    onChange={e => setCustomer(e.target.value)}
                                />
                            </Box>
                            <TextField size="small" type="date" label="Seen since (end date)"
                                       value={endDate} onChange={e => setEndDate(e.target.value)}
                                       InputLabelProps={{ shrink: true }} sx={{ minWidth: 185 }} />
                            <Box sx={{ flex: 1 }} />
                            {rows.length > 0 && (
                                <Typography variant="body2" color="text.secondary">
                                    {totalOwn.toLocaleString()} own · {totalComp.toLocaleString()} competitors ·
                                    {' '}cobertura de categoría {(coverage * 100).toFixed(1)}%
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {cid && (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
                                <Tab label="Overview (own vs competitors)" />
                                <Tab label="By competitor" />
                            </Tabs>
                            {tab === 1 && (
                                <TextField select size="small" label="Competitor site" sx={{ minWidth: 220 }}
                                           value={compSite} onChange={e => setCompSite(e.target.value)}
                                           disabled={!compSites.length}>
                                    {compSites.map(sn => <MenuItem key={sn} value={sn}>{sn}</MenuItem>)}
                                </TextField>
                            )}
                        </Box>
                    </Grid>
                )}
                {!cid ? (
                    <Grid item xs={12}>
                        <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                            <IconSeeker icon="layer-group" sx={{ fontSize: 48, mb: 1 }} />
                            <Typography>Select a customer to see the category distribution of its own and competitor catalogs.</Typography>
                        </Box>
                    </Grid>
                ) : loading ? (
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
                    </Grid>
                ) : (
                    <Grid item xs={12}>
                        <Card variant="outlined">
                            {rows.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>No data.</Box>
                            ) : tab === 1 ? (
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Category</TableCell>
                                            <TableCell align="right">Products</TableCell>
                                            <TableCell sx={{ width: '35%' }}>Share of {compSite || 'site'}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {siteRows.map(r => {
                                            const share = siteTotal > 0 ? r.count / siteTotal : 0
                                            return (
                                                <TableRow key={r.category} hover>
                                                    <TableCell><CategoryLabel cat={r.category} /></TableCell>
                                                    <TableCell align="right">{r.count.toLocaleString()}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LinearProgress variant="determinate" value={share * 100}
                                                                            sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                                                            <Typography variant="caption" sx={{ minWidth: 44 }}>
                                                                {(share * 100).toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Category</TableCell>
                                            <TableCell align="right">Own products</TableCell>
                                            <TableCell align="right">Competitor products</TableCell>
                                            <TableCell sx={{ width: '30%' }}>Share (own + competitors)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map(r => {
                                            const total = (r.own || 0) + (r.competitors || 0)
                                            const share = (totalOwn + totalComp) > 0 ? total / (totalOwn + totalComp) : 0
                                            return (
                                                <TableRow key={r.category} hover>
                                                    <TableCell><CategoryLabel cat={r.category} /></TableCell>
                                                    <TableCell align="right">{(r.own || 0).toLocaleString()}</TableCell>
                                                    <TableCell align="right">
                                                        {r.breakdown && r.breakdown.length > 0 ? (
                                                            <Tooltip arrow title={
                                                                <Box>
                                                                    {r.breakdown.map(b => (
                                                                        <Box key={b.site} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                                                            <span>{b.site}</span><span>{b.count.toLocaleString()}</span>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            }>
                                                                <Typography variant="body2" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                                                                    {(r.competitors || 0).toLocaleString()}
                                                                </Typography>
                                                            </Tooltip>
                                                        ) : (
                                                            <Typography variant="body2">{(r.competitors || 0).toLocaleString()}</Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <LinearProgress variant="determinate" value={share * 100}
                                                                            sx={{ flex: 1, height: 8, borderRadius: 4 }} />
                                                            <Typography variant="caption" sx={{ minWidth: 44 }}>
                                                                {(share * 100).toFixed(1)}%
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </Card>
                    </Grid>
                )}
            </Grid>
        </PageWrapper>
    )
}
